<template>
  <div v-click-outside="closeMenu" class="ak-masthead" :class="rootClasses">
    <a class="ak-masthead__nav-toggle" @click.prevent="openMenu">
      <ak-icon type="navicon"></ak-icon>
    </a>
    <slot name="brand" />
    <div class="ak-masthead__inner">
      <div class="ak-masthead__start">
        <slot name="start" />
      </div>
      <nav class="ak-masthead__nav">
        <slot name="middle" />
      </nav>
    </div>
    <div class="ak-masthead__end">
      <slot name="end" />
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import clickOutside from '../../directives/clickOutside'
import Icon from '../icon/Icon.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-masthead></ak-masthead>
   */
  name: 'AkMasthead',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
  },

  /**
   * @type {Object}
   * @description A collection of directives that this component is
   * dependant on.
   */
  directives: {
    clickOutside,
  },

  provide() {
    return {
      akMasthead: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description Determines if the navbar mobile nav is open or not
     */
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const open = ref(props.isOpen)
    const isMastHead = ref(true)

    /**
     * @return {String}
     * @description Returns the root elements classes
     */
    const rootClasses = computed(() => {
      return {
        'is-open': open.value,
      }
    })

    /**
     * @description Opens the mobile navigation bar if it is closed
     */
    const openMenu = () => {
      if (open.value === false) {
        open.value = true
      }
    }

    /**
     * @description Closes the mobile navigation bar if it is open
     */
    const closeMenu = () => {
      if (open.value === true) {
        open.value = false
      }
    }

    return {
      isMastHead,
      open,
      rootClasses,
      openMenu,
      closeMenu,
    }
  },
})
</script>
<style lang="scss">
@import '../../scss/_settings.scss';
.ak-masthead {
  display: flex;
  background-color: $color-grey-dark;
  height: 50px;

  &__nav-toggle {
    display: none;

    @media screen and #{$mq-nav} {
      color: #fff;
      display: flex;
      align-items: center;
      padding: 0 $spacing-tiny;
      cursor: pointer;
      font-size: 2rem;
    }
  }

  &__nav {
    display: flex;
    @media screen and #{$mq-nav} {
      flex-direction: column;
    }
  }

  &__inner {
    display: flex;
    @media screen and #{$mq-nav} {
      background-color: $color-grey-dark;
      flex-direction: column;
      transition: $transition;
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      width: 300px;
      transform: translateX(-100%);
      z-index: 20;

      .is-open & {
        transform: translateX(0);
      }
    }
  }

  &__start {
    display: flex;
    align-items: center;
    color: #fff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: $spacing-small;
    @media screen and #{$mq-nav-min} {
      padding: 0 $spacing-small;
      border-bottom: 0;
      border-left: 1px solid rgba(255, 255, 255, 0.1);
      border-right: 1px solid rgba(255, 255, 255, 0.1);
      margin-right: $spacing-tiny;
    }
  }

  &__end {
    display: flex;
    margin-left: auto;
  }
}
</style>
